/**
 * Data Sources settings — TASK-851
 *
 * One page that renders whatever `GET /api/sources/catalog` returns and knows about no vendor.
 * That is the deliverable, not a detail: `EmailConnectionSettings` beside this file hardcodes
 * `PROVIDERS`, so the one working connect flow in the product cannot gain a provider without a
 * React change. Adding a `case 'morningstar'` here would rebuild exactly that.
 *
 * Everything a card shows comes from the server's composition of registry + connection +
 * manifest (`shared/sources/catalog.py`). The labels on configuration inputs are the manifest's
 * own strings — "Your Morningstar endpoint" is a manifest value, not a React one — because a
 * per-account endpoint that needs a frontend change is the thing being removed.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Database,
  Info,
  Loader2,
  RefreshCw,
  XCircle,
} from 'lucide-react';
import {
  dataSourcesService,
  type DataSourceCard,
} from '../../services/dataSourcesService';

/** A connection time is not a sync time (TASK-825), so the two are never merged into one line. */
function formatWhen(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

function SyncHealth({ source }: { source: DataSourceCard }) {
  if (!source.connected) return null;

  return (
    <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
      <dt className="text-gray-500">Connected</dt>
      <dd className="text-gray-700">{formatWhen(source.connected_at)}</dd>

      <dt className="text-gray-500">Last sync</dt>
      <dd className={source.never_synced ? 'text-amber-700 font-medium' : 'text-gray-700'}>
        {/*
          The whole point of AC4. A source that has never synced says so, rather than showing
          its connection time in this slot — which is how a lane that has never run looks
          healthy. `never_synced` is a server field, not an inference from a null date.
        */}
        {source.never_synced ? 'Never synced' : formatWhen(source.last_sync_at)}
      </dd>

      {source.last_sync_status && (
        <>
          <dt className="text-gray-500">Status</dt>
          <dd className="text-gray-700">{source.last_sync_status}</dd>
        </>
      )}
    </dl>
  );
}

function ConfigFields({
  source,
  values,
  onChange,
}: {
  source: DataSourceCard;
  values: Record<string, string>;
  onChange: (field: string, value: string) => void;
}) {
  if (source.config_fields.length === 0) return null;

  return (
    <div className="mt-3 space-y-2">
      {source.config_fields.map((field) => (
        <label key={field.name} className="block">
          <span className="text-xs font-medium text-gray-700">
            {/* The manifest's label, verbatim. */}
            {field.label}
            {field.provided && (
              <span className="ml-2 font-normal text-gray-400">(already supplied)</span>
            )}
          </span>
          <input
            type={field.secret ? 'password' : 'text'}
            value={values[field.name] ?? ''}
            onChange={(e) => onChange(field.name, e.target.value)}
            placeholder={field.provided ? '••••••••' : ''}
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                       focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </label>
      ))}
    </div>
  );
}

function SourceCard({
  source,
  onChanged,
}: {
  source: DataSourceCard;
  onChanged: () => void;
}) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<null | 'connect' | 'test' | 'disconnect' | 'toggle'>(null);
  const [error, setError] = useState<string | null>(null);
  const [probe, setProbe] = useState<string | null>(null);

  const run = async (kind: NonNullable<typeof busy>, fn: () => Promise<void>) => {
    setBusy(kind);
    setError(null);
    setProbe(null);
    try {
      await fn();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong');
    } finally {
      setBusy(null);
    }
  };

  const handleConnect = () =>
    run('connect', async () => {
      const result = await dataSourcesService.connect(source.name, values);
      if (result.authorization_url) {
        // The vendor owns the next step; the callback returns through the gateway.
        window.location.href = result.authorization_url;
        return;
      }
      // TASK-879: the server says whether the VENDOR accepted these credentials, and the card
      // repeats it rather than inferring success from a 2xx. A transport with no live probe
      // stores the connection and says nothing was checked — a card that claimed otherwise
      // would be the overclaiming this epic exists to remove.
      if (result.verification_note) setProbe(result.verification_note);
      setValues({});
      onChanged();
    });

  const handleTest = () =>
    run('test', async () => {
      const result = await dataSourcesService.test(source.name);
      setProbe(result.ok ? 'Connection is working.' : result.message || 'The probe failed.');
      onChanged();
    });

  const handleDisconnect = () =>
    run('disconnect', async () => {
      await dataSourcesService.disconnect(source.name);
      onChanged();
    });

  const handleToggle = () =>
    run('toggle', async () => {
      await dataSourcesService.setEnabled(source.name, !source.enabled);
      onChanged();
    });

  // TASK-879 / DEC-847-7. The entitlement term is gone: the flag it read was hard-coded empty
  // server-side, so this branch hid the credential form on every account forever while the card
  // above it said "add the OAuth client ID". Credentials are the entitlement now, and the
  // vendor checks them at connect time.
  //
  // What replaces it is TWO separate questions, because conflating them is what built the trap:
  //
  //   canSetUp   — is there a form to fill in at all? Only "the connector is not built" can
  //                answer no. NOT availability: for a credential-bearing transport
  //                `available()` is false PRECISELY BECAUSE no credentials have been supplied,
  //                so gating the form on it is the same dead end wearing different clothes —
  //                you cannot supply credentials until you have supplied credentials.
  //   runnable   — can this source actually run right now? That gates what a CONNECTED card
  //                offers (a probe against an unavailable source is not useful), and nothing
  //                else. Disconnect is never gated: review caught an earlier version stranding
  //                a credential with no way to forget it, and Disable is not a substitute
  //                because it leaves the credential in place.
  const blockedByOperator = !source.available && source.unavailable_actor === 'operator';
  // The SERVER decides whether this card offers a form. Review caught the first version of this
  // ticket computing its own stricter rule here while the server's `can_connect` was not even
  // serialized — two answers to one question, and the client's answer hid the very fields
  // `connect` requires, reintroducing the dead end TASK-879 removed.
  //
  // `?? source.implemented` is for an older server that does not send the field yet, not a
  // second opinion.
  const canSetUp = source.can_connect ?? source.implemented;
  const runnable = source.implemented && source.available;
  // An ORPHAN is `implemented: false` too — there is no manifest and nothing implements it —
  // but "the connector is not built yet" is the wrong sentence for it, and review caught it
  // printing directly above the right one. "Never built" and "was here and is gone" are the two
  // things AC4 exists to keep apart.
  const notBuiltReason =
    !source.implemented && !source.orphaned
      ? 'This source is declared but its connector is not built yet.'
      : null;
  // TASK-880. Two questions again, because "why can it not run" and "who can do something
  // about it" have different answers and the card behaves differently for each.
  //
  // An OPERATOR reason is shown whether or not the card is connected: "no Google OAuth app is
  // registered for this deployment" is the whole story of the Gmail card, and hiding it until
  // someone connects would leave the padlock that started this ticket. An ADVISOR reason on an
  // unconnected card is suppressed, because it says "supply your credentials" and the form
  // directly below already says that better.
  // Show the reason whenever the card is connected, OR whenever the server refuses to let it
  // be connected. Review of TASK-883: keying this on `blockedByOperator` alone meant a card the
  // server had refused for a reason with no named actor — "Kielis does not yet consume crm
  // data" — rendered with no form, no button and no explanation. If the server will not let you
  // connect, it has to say why.
  const refusedByServer = !source.connected && !canSetUp;
  const runtimeReason = !source.available
    ? source.connected || blockedByOperator || refusedByServer
      ? source.unavailable_reason || 'This source cannot run right now.'
      : null
    : null;

  // Setup help is about what to BUY and where the keys come from, so it belongs on any
  // unconnected card that has one — including an operator-blocked one, which is the card that
  // most needs the context and, before review, was the one card that lost it.
  const showSetupHelp = !source.connected && source.implemented && !!source.setup_help;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="flex items-center gap-2 font-semibold text-gray-900">
            {source.display_name}
            {source.connected && !source.never_synced && (
              <CheckCircle2 className="h-4 w-4 text-green-600" />
            )}
          </h3>
          <p className="mt-0.5 text-xs text-gray-500">
            {source.kind} · {source.transport} · v{source.version}
          </p>
        </div>
        {source.connected && !source.orphaned && (
          <button
            onClick={handleToggle}
            disabled={busy !== null}
            className="shrink-0 rounded-md border border-gray-300 px-2.5 py-1 text-xs
                       text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {source.enabled ? 'Disable' : 'Enable'}
          </button>
        )}
      </div>

      {/*
        Setup help, ABOVE the form rather than in front of it. This is the manifest's own
        `requires.description` — the same sentence that used to be the padlock's text. It tells
        the firm what to buy and where the keys come from while they are looking at the fields
        those keys go into.
      */}
      {showSetupHelp && (
        <div className="mt-3 flex items-start gap-2 rounded-md bg-blue-50 p-2.5">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-500" />
          <p className="text-xs text-blue-900">{source.setup_help}</p>
        </div>
      )}

      {notBuiltReason && (
        <div className="mt-3 flex items-start gap-2 rounded-md bg-gray-50 p-2.5">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
          <p className="text-xs text-gray-600">{notBuiltReason}</p>
        </div>
      )}

      {runtimeReason && (
        <div className="mt-3 flex items-start gap-2 rounded-md bg-amber-50 p-2.5">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <div className="text-xs text-amber-900">
            <p>{runtimeReason}</p>
            {/*
              Who acts. Without this an advisor reads "no Google OAuth app is registered" and
              starts looking for the field to put one in — there isn't one, and there should
              not be. Naming the owner of the fix is what turns a dead padlock into a
              five-minute ops request.
            */}
            {source.unavailable_actor === 'operator' && (
              <p className="mt-1 text-amber-800/80">
                Your administrator configures this — there is nothing to do on this page.
              </p>
            )}
          </div>
        </div>
      )}

      {/*
        Shown when the card can be set up, AND on a CONNECTED source that still has fields.
        Review: gating on `!connected` alone meant a connected source's configuration could
        never be edited — so a rotated vendor key left the card erroring on every sync with no
        route back but Disconnect, which destroys the row, its credential and its sync history.
        It also made `(already supplied)` and the •••••••• placeholder unreachable, since
        `provided` can only be true once a connection row exists.

        Not an orphan: there is nothing to configure on a source that is gone.
      */}
      {(canSetUp || (source.connected && !source.orphaned)) &&
        source.config_fields.length > 0 && (
        <ConfigFields
          source={source}
          values={values}
          onChange={(field, value) => setValues((v) => ({ ...v, [field]: value }))}
        />
      )}

      {/* TASK-889: the whole reason this card exists. An advisor about to forget a connection
          is entitled to know whether a credential goes with it — the bool, never the value. */}
      {source.orphaned && source.has_credentials && (
        <p className="mt-2 text-xs text-gray-600">
          A stored credential will be removed with this connection.
        </p>
      )}

      <SyncHealth source={source} />

      {source.last_error && (
        <p className="mt-2 flex items-start gap-1.5 text-xs text-red-700">
          <XCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {source.last_error}
        </p>
      )}
      {error && (
        <p className="mt-2 flex items-start gap-1.5 text-xs text-red-700">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {error}
        </p>
      )}
      {probe && <p className="mt-2 text-xs text-gray-700">{probe}</p>}

      <div className="mt-3 flex gap-2">
        {/* Connect is offered wherever there is a form to submit — the vendor is the gate.
            On a connected source the same endpoint updates the stored credentials, so the
            button says that instead. `upsert` merges secrets, so supplying only the rotated
            one keeps the rest. */}
        {(canSetUp || (source.connected && !source.orphaned &&
                       source.config_fields.length > 0)) && (
          <button
            onClick={handleConnect}
            disabled={busy !== null || (source.connected && Object.keys(values).length === 0)}
            className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white
                       hover:bg-blue-700 disabled:opacity-50"
          >
            {busy === 'connect'
              ? (source.connected ? 'Updating…' : 'Connecting…')
              : (source.connected ? 'Update credentials' : 'Connect')}
          </button>
        )}
        {/* Testing an unavailable source is not useful; disconnecting one always is. And for
            an orphan there is nothing left to probe — the source is gone (TASK-889). */}
        {source.connected && runnable && !source.orphaned && (
          <button
            onClick={handleTest}
            disabled={busy !== null}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700
                       hover:bg-gray-50 disabled:opacity-50"
          >
            {busy === 'test' ? 'Testing…' : 'Test connection'}
          </button>
        )}
        {source.connected && (
          <button
            onClick={handleDisconnect}
            disabled={busy !== null}
            className="rounded-md border border-red-300 px-3 py-1.5 text-sm text-red-700
                       hover:bg-red-50 disabled:opacity-50"
          >
            {busy === 'disconnect' ? 'Disconnecting…' : 'Disconnect'}
          </button>
        )}
      </div>
    </div>
  );
}

export function DataSourcesSettings() {
  const [sources, setSources] = useState<DataSourceCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  //: A refresh that failed while cards are already on screen — shown above them, not instead.
  const [refreshError, setRefreshError] = useState<string | null>(null);

  const load = useCallback(async ({ isRefresh = false } = {}) => {
    try {
      setError(null);
      setSources(await dataSourcesService.getCatalog());
      setRefreshError(null);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Could not load data sources';
      // Review: a refresh that fails must not replace the cards with an error panel. It runs
      // after a SUCCESSFUL action — connect, disconnect, toggle — so wiping the page leaves the
      // advisor unable to tell whether the thing they just did actually landed, and with no
      // spinner to wait on either. On a refresh the stale cards stay and the failure is a
      // banner above them; only the very first load has nothing to keep.
      if (isRefresh) setRefreshError(message);
      else setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-6 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading data sources…
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <p className="flex items-center gap-2 text-sm text-red-800">
          <AlertTriangle className="h-4 w-4" />
          {error}
        </p>
        <button
          onClick={() => void load()}
          className="mt-2 flex items-center gap-1.5 text-xs text-red-700 hover:underline"
        >
          <RefreshCw className="h-3 w-3" /> Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
          <Database className="h-5 w-5" />
          Data Sources
        </h2>
        <p className="mt-1 text-sm text-gray-600">
          Connect the vendors and mailboxes this account is licensed for. Sources appear here
          from the catalog — no release is needed to add one.
        </p>
      </div>

      {refreshError && (
        <div className="flex items-start justify-between gap-3 rounded-lg border border-amber-200
                        bg-amber-50 p-3">
          <p className="flex items-start gap-2 text-sm text-amber-900">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            {/* The cards below are from before this failure, so say so — an advisor who just
                connected something needs to know whether what they are looking at reflects it. */}
            Could not refresh: {refreshError}. The sources below may be out of date.
          </p>
          <button
            onClick={() => void load({ isRefresh: true })}
            className="shrink-0 text-xs text-amber-800 hover:underline"
          >
            Retry
          </button>
        </div>
      )}

      {sources.length === 0 ? (
        // Says what it looked at, rather than rendering an empty grid that reads as "nothing
        // to connect" — the Digest's "Quiet night" problem in a different surface.
        <p className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
          The catalog is empty. No data sources are registered on this deployment.
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {sources.map((source) => (
            <SourceCard
              key={source.name}
              source={source}
              onChanged={() => void load({ isRefresh: true })}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default DataSourcesSettings;
